import { useEffect, useState } from 'react';

import {
    PutStateRequestBody,
    fetchGetStateByStateKey,
    fetchPutState
} from '@iwoca/lapi-client/edge';
import { CheckBox } from '@iwoca/orion';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import classNames from 'classnames';
import { produce } from 'immer';
import { FormattedMessage, useIntl } from 'react-intl';

import styles from './PennyTransferModal.module.css';
import * as IwDataDogLogger from '../../../../../iwLogger/IwDataDogLogger';
import { useSiteContext } from '../../../../../iwQueryClient/useSiteContext';
import { useStateKey } from '../../../../../iwQueryClient/useStateKey';
import FallbackErrorModal from '../../../../modals/fallbackErrorModal/FallbackErrorModal';
import MainContentPending from '../../../../pageStructure/mainContentPending';

export const PennyTransferModal = () => {
    const intl = useIntl();
    const [pennyTransferSent, setPennyTransferSent] = useState(false);
    const [isCheckboxEnabled, setIsCheckboxEnabled] = useState(true);
    const [pennyTransferSentPending, setPennyTransferSentPending] =
        useState(false);

    const stateKey = useStateKey();

    const siteContext = useSiteContext();

    const {
        data: customerStateResponse,
        isLoading: customerStateLoading,
        isError: customerStateError
    } = useQuery({
        queryKey: ['customer_state', stateKey],
        queryFn: () => fetchGetStateByStateKey({ stateKey })
    });

    const queryClient = useQueryClient();
    const saveCustomerState = useMutation({
        mutationFn: (state: PutStateRequestBody) =>
            fetchPutState({
                stateKey: stateKey,
                body: state
            }),
        onSuccess: async () => {
            await queryClient.invalidateQueries({
                queryKey: ['customer_state', stateKey]
            });
        }
    });

    useEffect(() => {
        if (customerStateResponse) {
            const isPennyTransferSent =
                //@ts-expect-error: UI does not exist on Customer State Response LAPI client type
                customerStateResponse.data.ui?.draw_down?.penny_transfer_sent;

            if (isPennyTransferSent) {
                setIsCheckboxEnabled(false);
                setPennyTransferSent(true);
            }
        }
    }, [customerStateResponse]);

    if (customerStateLoading) {
        return <MainContentPending />;
    }
    if (customerStateError) {
        return <FallbackErrorModal />;
    }

    const pennyTransferAccountNumber =
        siteContext.data?.user_repayment_details?.account_number;
    const pennyTransferSortCode =
        siteContext.data?.user_repayment_details?.sort_code;
    const pennyTransferAccountName =
        siteContext.data?.user_repayment_details?.owner_name;

    const handlePennyTransferSent = async () => {
        setPennyTransferSent(true);
        setPennyTransferSentPending(true);
        try {
            const pennyTransferSentDate = new Date().toISOString();

            const newCustomerState = produce(customerStateResponse, (draft) => {
                //@ts-expect-error: ui does not exist on type definition
                draft.data.ui = draft.data.ui || {};
                //@ts-expect-error: ui does not exist on type definition
                draft.data.ui.draw_down = draft.data.ui.draw_down || {};
                //@ts-expect-error: ui does not exist on type definition
                draft.data.ui.draw_down.penny_transfer_sent =
                    pennyTransferSentDate;
            });
            await saveCustomerState.mutateAsync(newCustomerState);

            setIsCheckboxEnabled(false);
            setPennyTransferSentPending(false);
        } catch (error: any) {
            IwDataDogLogger.logError(
                error.message,
                {
                    tags: { reporter: 'handle_penny_transfer_sent' }
                },
                error
            );
        }
    };

    return (
        <div className={styles.PennyTransferModal}>
            <div className={styles.header}>
                <FormattedMessage
                    id="_.modal.penny_transfer.title"
                    defaultMessage="Send us a penny"
                >
                    {(...msg) => <h1 className={styles.title}>{msg}</h1>}
                </FormattedMessage>
                <FormattedMessage
                    tagName="div"
                    id="_.modal.penny_transfer.subTitle"
                    defaultMessage="To verify your business bank details, log on to your online banking and transfer us a penny to the following account:"
                />
            </div>
            {/* transfer Details */}
            <section
                className={styles.transferDetails}
                data-testid="transfer-details"
            >
                <div>
                    <FormattedMessage
                        tagName="strong"
                        id="_.modal.penny_transfer.account_number"
                        defaultMessage="Account number"
                    />
                    <p
                        className={classNames(
                            styles.transferInfo,
                            'fs-exclude'
                        )}
                    >
                        {pennyTransferAccountNumber}
                    </p>
                </div>
                <div>
                    <FormattedMessage
                        tagName="strong"
                        id="_.modal.penny_transfer.sort_code"
                        defaultMessage="Sort code"
                    />
                    <p
                        className={classNames(
                            styles.transferInfo,
                            'fs-exclude'
                        )}
                    >
                        {pennyTransferSortCode}
                    </p>
                </div>
                <div>
                    <FormattedMessage
                        tagName="strong"
                        id="_.modal.penny_transfer.account_name"
                        defaultMessage="Account name"
                    />
                    <p
                        className={classNames(
                            styles.transferInfo,
                            'fs-exclude'
                        )}
                    >
                        {pennyTransferAccountName}
                    </p>
                </div>
            </section>
            {/* transfer Details end*/}
            <CheckBox
                label={intl.formatMessage({
                    id: '_.modal.penny_transfer.sent_penny',
                    defaultMessage: 'I transferred a penny'
                })}
                onChange={() => handlePennyTransferSent()}
                checked={pennyTransferSent}
                disabled={!isCheckboxEnabled}
            />
            {!isCheckboxEnabled && (
                <div className={styles.verifyingSubTitle}>
                    <FormattedMessage
                        id="_.modal.penny_transfer.verifying.subTitle"
                        defaultMessage="Thanks! Continue to the next step while we're checking the transaction and we'll let you know once we've verified it."
                    />
                </div>
            )}
            {pennyTransferSentPending && (
                <div className={styles.verifyingSubTitle}>
                    <FormattedMessage
                        id="_.modal.penny_transfer.verifying.title"
                        defaultMessage="We're verifying the transaction"
                    />
                </div>
            )}
        </div>
    );
};
